import React, { useState, useCallback, useEffect } from 'react';
import { useAnvilProps } from "frontend/hooks";
import { Box, Center, Heading, Link, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { generatePath, Redirect, useHistory } from 'react-router-dom';
import { ProductCard} from "../components";
import { generatePricePeriodText } from "frontend/order_tunnel/utils/braintree";
import { RichText } from "frontend/wagtail/components/RichText";
import { userStatus } from "core_src/js/user_config";
import { coreUrls } from "core_src/js/core_urls_config";
import { getRequest } from "../services";
import { analytics } from "anvil-analytics/analytics";
import {ZipCodeScreen} from './ZipCodeScreen';

export default function PlansScreen() {
    const { messages, product_listings, settings } = useAnvilProps();
    const history = useHistory();

    const [isZipCodeScreenVisible, setZipCodeScreenVisible] = useState(false);
    const [currentListing, setCurrentListing] = useState(null);
    const [zipcodes, setZipcodes] = useState(null);

    const getZipCodes = async (urlEndpoint) => {
        let response = await getRequest(urlEndpoint);
        return response;
    };

    const handleProceedFromZipCodeScreen = () => {
        // Hide the ZipCodeScreen after a successful submission
        setZipCodeScreenVisible(false);
        proceedToBilling(currentListing.listing, currentListing.index);
    };

    const proceedToBilling = (listing, index) => {
        analytics.track('AddToCart', {
            product: {
                id: listing.braintree ? listing.braintree.id : listing.rate_card_pk,
                name: listing.braintree ? listing.braintree.name : listing.rate_card_title,
                price: listing.braintree ? listing.braintree.upfront_cost : listing.upfront_cost,
                currency: listing.braintree ? listing.braintree.currency_iso_code : 'USD',
                category: listing.is_print ? 'Print & Digital' : 'Digital',
                trial: listing.braintree ? !!(listing.braintree.trial_period) : listing.trial,
                discount: listing.braintree ? !!(listing.braintree.discounts) : listing.discount,
                quantity: 1,
                position: index + 1,
                type: 'Subscription'
            },
            step: 1
        });

        setZipcodes('');
        setZipCodeScreenVisible(false);

        const path = generatePath('/billing/:product_pk', { product_pk: (listing.product_pk || listing.rate_card_pk) });
        history.push(path);
    };

    const navigateToListing = useCallback(async (listing, index) => {
        setCurrentListing({ listing, index });
        if (listing.rate_card_pk) {
            const needsZipcodeResponse = await getZipCodes(`${coreUrls.order_tunnel_api}rate-card/${listing.rate_card_pk}/`);
            if (needsZipcodeResponse && needsZipcodeResponse.zipcodes !== '') {
                setZipcodes(needsZipcodeResponse.zipcodes);
                setZipCodeScreenVisible(true);
            } else {
                proceedToBilling(listing, index);
            }
        } else {
                proceedToBilling(listing, index);
        }
    }, [history]);

    useEffect(() => {
        analytics.track('ViewProducts', {
            products: product_listings.map((product, index) => ({
                'id': product.braintree ? product.braintree.id : product.rate_card_pk,
                'name': product.braintree ? product.braintree.name : product.rate_card_title,
                'price': product.braintree ? product.braintree.upfront_cost : product.upfront_cost,
                'currency': product.braintree ? product.braintree.currency_iso_code : 'USD',
                'category': product.is_print ? 'Print & Digital' : 'Digital',
                'trial': product.braintree ? !!(product.braintree.trial_period) : product.trial,
                'discount': product.braintree ? !!(product.braintree.discounts) : product.discount,
                'quantity': 1,
                'position': index + 1,
                'type': 'Subscription'
            })),
            step: 0
        });
    }, [product_listings]);

    if (userStatus.has_paid_subscription && !settings.is_admin_preview) {
        return <Redirect to={'/already-subbed'} />;
    }

    return (
        <Box>
            {isZipCodeScreenVisible ? (
                <ZipCodeScreen
                    zipcodes={zipcodes}
                    onProceed={handleProceedFromZipCodeScreen}
                    onBack={() => setZipCodeScreenVisible(false)}  // Back to plan selection
                />
            ) : (
                <>
                    <Center mb={[4, 8.16]}>
                        <Box>
                            <Heading>{product_listings.length ? messages.available_plans_header : messages.no_available_plans_header}</Heading>
                            <RichText data={product_listings.length ? messages.available_plans_body : messages.no_available_plans_body} />
                        </Box>
                    </Center>
                    <Wrap justify="center" spacing={[4, null, 8]}>
                        {product_listings.map((listing, index) => (
                            <WrapItem key={listing.product_pk || listing.rate_card_pk}>
                                <ProductCard
                                    title={listing.title}
                                    price={listing.price}
                                    originalPrice={`${listing.original_price ? `$${listing.original_price}` : ""}`}
                                    pricePeriodText={listing.price_period_override || (listing.braintree && generatePricePeriodText(listing))}
                                    buttonText={listing.button_text}
                                    primaryColor={listing.primary_color_override}
                                    specialOffer={listing.special_offer}
                                    specialOfferColor={listing.special_offer_color}
                                    features={listing.features}
                                    buttonOnClick={() => navigateToListing(listing, index)}
                                    extraProductText={listing.extra_description_text}
                                />
                            </WrapItem>
                        ))}
                    </Wrap>
                    <Center mt={12}>
                        <Text>
                            Already have a subscription?
                            <Link href={coreUrls.account_link_subscription}> Request digital access.</Link>
                        </Text>
                    </Center>
                </>
            )}
        </Box>
    );
}
